import React, { useRef, forwardRef, useImperativeHandle, useMemo, useCallback } from 'react';
import { Card, Button, List, Toast } from 'antd-mobile';
import { formatLocalizedDateTime } from '../../../../utils/dateFormatUtils';
import DeliveryPhoneAndNavigate, { DeliveryPhoneAndNavigateRef } from '../../../delivery/delivering/components/DeliveryPhoneAndNavigate';
import { copyToClipboard } from '../../../../utils/copyToClipboard';
import { useTranslation } from 'react-i18next';
import { Parcel } from '../../graphql/types/DeliveryTaskType';
import {maskPhoneNumber} from "../../../../utils/phoneUtils";
import {closeNavigationModals as closeNavigationModalsUtil, forceCloseNavigationModal} from "../../../../utils/navigationUtils";

interface DspParcelDetailCardProps {
    parcel: Parcel;
    onClose?: () => void;
}

export interface DspParcelDetailCardRef {
    closeLongPressModal: () => void;
}

const DspParcelDetailCard = forwardRef<DspParcelDetailCardRef, DspParcelDetailCardProps>(({ parcel, onClose }, ref) => {
    const { t } = useTranslation();
    // 从 parcel 中获取 clusterInfo
    const clusterInfo = parcel.clusterInfo;
    const deliveryPhoneAndNavigateRef = useRef<DeliveryPhoneAndNavigateRef>(null);

    // 暴露关闭长按弹窗的方法给父组件
    useImperativeHandle(ref, () => ({
        closeLongPressModal: () => {
            // 单个包裹模式：关闭单个导航弹窗
            deliveryPhoneAndNavigateRef.current?.closeLongPressModal();
            // 集群模式：通过 DOM 查询关闭所有打开的导航弹窗（iOS 18 增强）
            closeNavigationModalsUtil();
            // iOS 18 需要更强制的方式关闭弹窗，使用 requestAnimationFrame 确保状态更新后再执行 DOM 操作
            requestAnimationFrame(() => {
                forceCloseNavigationModal(
                    t('pages.delivery.orderDetail.selectMapForThisTimeTitle') || undefined,
                    t('pages.delivery.orderDetail.mapCancel') || undefined
                );
            });
        }
    }), []);

    const handleCopyAddress = useCallback(async (targetParcel?: Parcel) => {
        const addressToCopy = targetParcel?.address || parcel.address;
        
        if (!addressToCopy || addressToCopy.trim() === '') {
            Toast.show({
                content: t('pages.dsp.deliveryTaskDetails.noAddress'),
                position: 'bottom',
            });
            return;
        }

        try {
            const success = await copyToClipboard(addressToCopy);
            if (success) {
                Toast.show({
                    content: t('pages.dsp.deliveryTaskDetails.addressCopied'),
                    position: 'bottom',
                });
            } else {
                Toast.show({
                    content: t('pages.dsp.deliveryTaskDetails.copyFailed'),
                    position: 'bottom',
                });
            }
        } catch (error) {
            console.error('Copy address failed:', error);
            Toast.show({
                content: t('pages.dsp.deliveryTaskDetails.copyFailed'),
                position: 'bottom',
            });
        }
    }, [parcel.address, t]);


    // 渲染单个包裹卡片
    const renderSingleParcelCard = (singleParcel: Parcel, isSingleMode: boolean = false) => (
        <Card className="m-2 rounded-lg overflow-y-auto flex flex-col">
            {/* 置顶的 trackingNumber */}
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-700 break-all word-break-break-all hyphens-auto leading-relaxed">{singleParcel?.trackingNumber}</h3>
            </div>
            {/* 主要内容区域 */}
            <div className="flex items-start px-4 py-4 gap-4">
                {/* 左边居中的 stopNumber - 与 trackingNumber 左对齐 */}
                <div className="flex flex-col items-center justify-center flex-shrink-0 w-20 h-24">
                    <p className="text-2xl font-bold whitespace-nowrap mb-2">{singleParcel.stopNumber}</p>
                    <p className="text-sm text-gray-500 text-center leading-tight">{t('pages.dsp.deliveryTaskDetails.stop')}</p>
                </div>

                {/* 详细信息 - 尽量展开 */}
                <div className="flex flex-col gap-2 flex-1 min-w-0">
                    <h3 className="text-lg font-medium break-words">{singleParcel.recipientName}</h3>
                    <div className="text-sm text-gray-500 break-all">{`${maskPhoneNumber(singleParcel.recipientPhoneNumber)}${singleParcel.recipientPhoneExtension ? `-${singleParcel.recipientPhoneExtension}` : ''}`}</div>
                    <div className="text-blue-500 text-base">
                        <div className="flex items-start gap-2">
                            <span className="break-words flex-1 min-w-0 leading-relaxed">{singleParcel.address}</span>
                            <button
                                onClick={() => handleCopyAddress(singleParcel)}
                                className="p-1 flex items-center justify-center flex-shrink-0"
                                style={{ minWidth: '24px', height: '24px' }}
                            >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M7 4V2C7 1.45 7.45 1 8 1H20C20.55 1 21 1.45 21 2V16C21 16.55 20.55 17 20 17H18V19C18 20.1 17.1 21 16 21H4C2.9 21 2 20.1 2 19V7C2 5.9 2.9 5 4 5H7V4M7 7H4V19H16V7H7Z"/>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* 拨打电话 / 导航 */}
            <DeliveryPhoneAndNavigate
                ref={isSingleMode ? deliveryPhoneAndNavigateRef : undefined}
                phone={(singleParcel as any).recipientPhoneNumber || ''}
                address={singleParcel.address || ''}
                tracking={singleParcel.trackingNumber}
                latitude={singleParcel.latitude}
                longitude={singleParcel.longitude}
                stopNumber={singleParcel.stopNumber}
                fullName={singleParcel.recipientName}
                subStopNumber={singleParcel.subStopNumber}
            />

            {/* 详细信息列表 */}
            <List
                style={{
                    '--border-top': 'none',
                    '--border-bottom': 'none',
                    '--padding-left': '0px',
                    '--padding-right': '0px',
                }}
            >
                {/* 路线 */}
                <List.Item title={t('pages.dsp.deliveryTaskDetails.route')} extra={singleParcel.routeName} />
                {/* 派单时间 */}
                {(singleParcel as any).deliverAt && (
                    <List.Item title={t('pages.dsp.deliveryTaskDetails.deliverAt')} extra={formatLocalizedDateTime(new Date((singleParcel as any).deliverAt))} />
                )}
                {/* 派送尝试次数 */}
                {typeof (singleParcel as any).deliveryAttempts === 'number' && (
                    <List.Item title={t('pages.dsp.deliveryTaskDetails.deliveryAttempts')} extra={`${(singleParcel as any).deliveryAttempts} out of 3`} />
                )}
            </List>
        </Card>
    );

    // 获取要渲染的包裹列表（使用 useMemo 缓存，避免每次渲染都重新计算）
    const parcelsToRender = useMemo(() => {
        return clusterInfo?.isCluster && clusterInfo?.clusterAddresses 
        ? clusterInfo.clusterAddresses 
        : [parcel];
    }, [clusterInfo?.isCluster, clusterInfo?.clusterAddresses, parcel]);

    return (
        <div className="flex flex-col h-full">
            {/* 包裹内容区域 */}
            <div className={clusterInfo?.isCluster ? "max-h-[70vh] overflow-y-auto" : "flex-1"}>
                {clusterInfo?.isCluster ? (
                    // 集群模式：显示多个包裹卡片
                    <div className="space-y-4 p-4">
                        {parcelsToRender.map((singleParcel, index) => (
                            <div key={singleParcel.id || index} className="border border-gray-200 rounded-lg">
                                {renderSingleParcelCard(singleParcel)}
                            </div>
                        ))}
                    </div>
                ) : (
                    // 单个包裹模式：显示单个包裹卡片
                    renderSingleParcelCard(parcel, true)
                )}
            </div>

            {/* 底部操作按钮 */}
            <div className="flex px-4 py-2 pb-4 gap-4">
                <Button
                    color='primary'
                    onClick={onClose}
                    block
                >
                    {t('pages.dsp.deliveryTaskDetails.close')}
                </Button>
            </div>
        </div>
    );
});

DspParcelDetailCard.displayName = 'DspParcelDetailCard';

export default DspParcelDetailCard;